import React from 'react';
import { Store, MapPin, Users, Tag, BarChart3, Gift, ArrowRight, UserPlus, FileEdit, Megaphone, Sparkles } from 'lucide-react';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { PlansCarousel } from './PlansCarousel';

const CATEGORIES = ['Ristoranti', 'Escursioni', 'Tour', 'Noleggi', 'Spiagge', 'Musei', 'Wine Experience', 'Servizi', 'Shopping'];

const PLANS = [
  {
    id: 'starter', name: 'Starter', price: '3,99', badge: null, cta: 'Inizia Gratis',
    tagline: 'Ideale per piccole attività.',
    includes: ['Scheda attività', 'Logo', 'Descrizione', 'Contatti', 'Google Maps', 'Categoria', 'Visibilità nelle strutture aderenti della zona'],
  },
  {
    id: 'business', name: 'Business', price: '9,99', badge: 'Più scelto', cta: 'Provalo Gratis',
    tagline: 'Tutto Starter, più visibilità e strumenti di promozione.',
    includes: ['Tutto il piano Starter', 'Galleria immagini', 'Badge “Partner Consigliato”', 'Promozioni', 'Coupon', 'Evidenza nelle ricerche', 'Priorità nella categoria', 'Maggiore visibilità'],
  },
  {
    id: 'premium', name: 'Premium', price: '14,99', badge: null, cta: 'Massimizza la tua visibilità',
    tagline: 'Tutto Business, più banner, analytics e priorità massima.',
    includes: ['Tutto il piano Business', 'Banner sponsorizzati', 'Massima priorità', 'Homepage evidenziata', 'Analytics', 'Numero visualizzazioni', 'Numero click', 'Report mensili', 'Supporto prioritario'],
  },
];

const STEPS = [
  { icon: UserPlus, title: 'Registrati gratuitamente' },
  { icon: FileEdit, title: 'Completa il profilo della tua attività' },
  { icon: Megaphone, title: 'Pubblica offerte e promozioni' },
  { icon: Users, title: 'Ricevi nuovi clienti dalle strutture Domira' },
];

const BENEFITS = [
  { icon: MapPin, title: 'Visibilità geolocalizzata', text: 'I turisti trovano la tua attività quando sono già arrivati.' },
  { icon: Users, title: 'Nuovi clienti', text: 'Raggiungi persone realmente interessate ai tuoi servizi.' },
  { icon: Tag, title: 'Promozioni', text: 'Pubblica offerte dedicate agli ospiti delle strutture aderenti.' },
  { icon: BarChart3, title: 'Analytics', text: 'Monitora visualizzazioni, click e performance della tua attività.' },
];

export function PartnerLandingSection() {
  return (
    <div data-testid="partner-landing-section">
      {/* INTRO + PLANS on secondary background */}
      <section id="partner" className="bg-paelbi-bg2 py-16 md:py-24">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-paelbi-brown">
              <Store size={14} /> Per le attività locali
            </span>
            <h2 className="mt-5 font-display text-3xl leading-tight text-paelbi-ink md:text-[40px]">
              Hai un’attività locale?<br />
              <span className="text-paelbi-brown">Fatti trovare dai turisti</span> nel momento esatto in cui stanno cercando cosa fare.
            </h2>
            <p className="mt-6 font-sans text-base leading-relaxed text-paelbi-ink/70">
              Ogni giorno gli ospiti delle strutture aderenti consultano Domira per scoprire:
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {CATEGORIES.map((c) => (
                <span key={c} className="rounded-full border border-paelbi-line bg-white px-3 py-1 text-sm text-paelbi-ink/75">{c}</span>
              ))}
            </div>
            <p className="mx-auto mt-6 max-w-2xl font-sans text-base leading-relaxed text-paelbi-ink/70">
              Domira porta la tua attività direttamente davanti a persone già presenti sul territorio e realmente interessate ai tuoi servizi.
              Non è semplice pubblicità. <span className="font-semibold text-paelbi-ink">È visibilità altamente qualificata.</span>
            </p>
            <div className="mt-8">
              <Button to="/partner/register" variant="primary" data-testid="partner-cta-top">Diventa Partner</Button>
            </div>
          </div>

          {/* Plan cards — same responsive carousel as Host */}
          <PlansCarousel plans={PLANS} ctaTo={(p) => `/partner/register?plan=${p.id}`} testidPrefix="partner-landing-plan" />

          {/* Trial banner */}
          <div className="mx-auto mt-10 flex max-w-2xl flex-col items-center gap-2 rounded-card border border-paelbi-line bg-white px-6 py-6 text-center sm:flex-row sm:justify-center sm:gap-4 sm:text-left">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-paelbi-bg2"><Gift size={24} className="text-paelbi-brown" /></span>
            <div>
              <p className="font-display text-lg text-paelbi-ink">30 giorni di prova gratuita</p>
              <p className="font-sans text-sm text-paelbi-ink/70">Nessuna carta di credito richiesta. Puoi annullare quando vuoi.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* COME FUNZIONA */}
      <section className="py-16 md:py-24">
        <Container>
          <h2 className="text-center font-display text-3xl text-paelbi-ink md:text-[32px]">Come funziona</h2>
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative flex flex-col items-center text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paelbi-brown font-display text-lg text-white">{i + 1}</span>
                <s.icon size={22} className="mt-4 text-paelbi-brown" />
                <p className="mt-3 max-w-[200px] font-sans text-sm text-paelbi-ink/75">{s.title}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight size={20} className="absolute -right-4 top-4 hidden text-paelbi-line lg:block" />
                )}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* BENEFICI */}
      <section className="bg-paelbi-bg2 py-16 md:py-24">
        <Container>
          <h2 className="text-center font-display text-3xl text-paelbi-ink md:text-[32px]">I vantaggi per la tua attività</h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-card border border-paelbi-line bg-white p-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-paelbi-bg2"><b.icon size={24} className="text-paelbi-brown" /></span>
                <h3 className="mt-4 font-display text-lg text-paelbi-ink">{b.title}</h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-paelbi-ink/70">{b.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="pb-16 md:pb-24">
        <Container>
          <div className="flex flex-col items-center gap-5 rounded-card bg-paelbi-ink px-6 py-14 text-center text-white">
            <Sparkles size={28} className="text-paelbi-accent" />
            <h2 className="max-w-2xl font-display text-3xl leading-tight md:text-[36px]">Porta nuovi clienti nella tua attività.</h2>
            <p className="max-w-xl font-sans text-base text-white/75">Diventa Partner Domira e inizia oggi stesso.</p>
            <Button to="/partner/register" variant="primary" className="mt-2" data-testid="partner-cta-final">Diventa Partner</Button>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default PartnerLandingSection;
